import React from 'react';
import './Legal.css';

function PrivacyPolicy() {
  return (
    <div className="legal page-shell">
      <span className="tag">Legal</span>
      <h1 className="page-title">Privacy Policy</h1>
      <p className="page-lead">Last updated: August 20, 2026</p>

      <div className="legal-section section-card">
        <h2>1. Overview</h2>
        <p>
          This policy explains how Vix Algo Trading Store collects, uses, and protects your information
          when you use our website, VixAlgoBuilder, VixAI, VixPremium, community features, product downloads,
          or support services.
        </p>

        <h2>2. Information We Collect</h2>
        <ul>
          <li>Account data: name, email, and login credentials.</li>
          <li>Purchase and subscription data: order IDs, product IDs, license records, payment confirmations, subscription status, renewal status, cancellation status, and billing provider references.</li>
          <li>Builder data: saved strategies, symbols, timeframes, rules, indicators, backtest settings, reports, exported EA metadata, and strategy vault records.</li>
          <li>VixAI data: prompts, assistant responses, generated strategy drafts, requested Builder actions, validation results, and conversation history.</li>
          <li>Community data: posts, comments, strategy snapshots, uploaded files, images, videos, PDFs, links, MT5 reports, tags, moderation status, and related discussion history.</li>
          <li>Support data: messages, attachments, ticket history, and account troubleshooting details.</li>
          <li>Cloud signal data: strategies you deploy to run in the cloud, the simulated trades and equity history they generate, deployment settings, visibility choices, marketplace listings, and the signals you follow.</li>
          <li>Alert delivery data: the channels you enable, your Telegram chat identifier if you link Telegram, the Discord webhook URL you provide, and a record of which alerts were sent to you so the same alert is not delivered twice.</li>
          <li>MT5 copier data: the access token issued to your MetaTrader terminal, your per-signal lot sizing choices, and — recorded when your terminal contacts us — the account number and broker name it reports, so you can tell where your token is being used.</li>
          <li>Live tracking data: if you link a MetaTrader account, the trading results that account reports to us — account number, broker, server, currency, leverage, balance, equity, and closed deal history for the account you linked.</li>
          <li>Imported EA data: the MQL5 source files you upload to run on our servers, the settings you choose for the run (symbol, timeframe, date range, deposit, inputs), and the report the run produces. The source itself is held only for the length of the run — see Data Retention below.</li>
          <li>Platform diagnostics: when an uploaded EA uses part of MetaTrader's programming language that our engine does not yet support, we record the name of the unsupported feature, the error message, the file name, the run settings, and your account identifier, so we can implement what is missing. We do not keep your code in these records.</li>
          <li>Usage data: device, browser, IP address, visitor identifier, page activity, feature usage, error logs, and basic analytics used to improve performance and security.</li>
        </ul>
        <p>
          We never ask for and never store your MetaTrader password or investor password. Live
          tracking and the copier work through a token you generate here and can revoke at any
          time; neither can withdraw funds, and the copier only ever closes positions it opened
          itself.
        </p>

        <h2>3. How We Use Information</h2>
        <ul>
          <li>To deliver downloads, licenses, receipts, subscriptions, VixPremium access, Builder features, VixAI, and community access.</li>
          <li>To save strategies, run Builder workflows, export EAs, manage strategy vaults, and keep your account history available.</li>
          <li>To process VixAI prompts and return assistant responses, drafts, setup guidance, validation notes, and Builder actions.</li>
          <li>To publish, moderate, rank, search, and manage community threads, comments, and shared files.</li>
          <li>To run cloud signals, evaluate them as each candle closes, keep their track records, and rank public signals in the marketplace.</li>
          <li>To send the alerts you asked for over the channels you enabled, and to serve your MetaTrader terminal the positions it should be copying.</li>
          <li>To compile and run the Expert Advisors you import, and to produce their backtest reports.</li>
          <li>To find and fix gaps in our MetaTrader engine, using the record of unsupported features described above. These records are read by our own team to decide what to build next; they are not used to profile you and are not shared with other users.</li>
          <li>To respond to support requests and chat messages.</li>
          <li>To improve product performance, reliability, security, and user experience.</li>
          <li>To comply with legal obligations and prevent abuse.</li>
        </ul>

        <h2>4. Sharing and Disclosure</h2>
        <p>
          We do not sell your personal information. We may share data with payment processors,
          hosting providers, storage providers, email services, analytics tools, AI model providers, and
          moderation or security services strictly to provide, protect, and improve our services.
          Community posts, comments, shared strategy snapshots, and files you choose to publish may be
          visible to other eligible community members.
        </p>
        <p>
          If you enable Telegram or Discord alerts, the content of those alerts is transmitted to
          Telegram or to the Discord server your webhook belongs to, and is then subject to their
          handling rather than ours. A Discord webhook posts into whichever channel you created it
          for — anyone with access to that channel can see your alerts, so do not use a webhook for
          a channel you share unless you intend that.
        </p>
        <p>
          Some pages embed third-party content, such as the MetaQuotes market-quote widget, and
          some links are affiliate links to brokers. Loading embedded content lets that provider
          see your IP address and set its own cookies, and following an affiliate link passes a
          referral identifier to that broker so the referral can be attributed. Embedded
          third-party content loads only after you accept optional cookies.
        </p>
        <p>
          Listing a signal in the marketplace makes its track record public: its name, symbol,
          timeframe, performance statistics, equity curve, and your display name. The strategy
          rules themselves are never exposed — only the trades the strategy took.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          Account data is retained while your account is active. Purchase, license, subscription, and
          billing records may be retained for accounting, tax, fraud-prevention, and compliance purposes.
          Saved strategies, VixAI conversations, support tickets, and community content remain available
          until you delete them, request deletion, or we remove them under our moderation and retention rules.
        </p>
        <p>
          MQL5 source you upload to be backtested is written to temporary storage for the run and
          deleted as soon as the run finishes, whether it succeeded or failed. We do not keep a copy,
          and it is never stored in our database. The report the run produced stays with your
          account. Where an EA used something our engine does not yet support, the diagnostic record
          we keep contains the name of the missing feature and the file name — never the code
          itself — and is retained until the gap is fixed and for a reasonable period afterwards so
          we can confirm it stays fixed.
        </p>

        <h2>6. Security</h2>
        <p>
          We use industry-standard security practices to protect your information. However, no system
          can guarantee absolute security.
        </p>

        <h2 id="cookies">7. Cookies</h2>
        <p>
          We use essential cookies and similar technologies to keep the site secure, remember sessions,
          manage subscriptions, protect premium features, and operate the Builder. Optional cookies and
          analytics identifiers help us understand traffic, errors, and feature usage. You can accept or
          reject optional cookies at any time where controls are available.
        </p>

        <h2>8. Your Rights</h2>
        <p>
          You may request access, correction, export, or deletion of your personal data. Some records may
          be retained where required for accounting, security, dispute handling, legal obligations, or
          legitimate business records. Contact us at support@vixalgotrading.com to exercise these rights.
        </p>

        <h2>9. Updates</h2>
        <p>
          We may update this policy periodically. Continued use of the site indicates acceptance of the
          updated policy.
        </p>

        <h2>10. Contact</h2>
        <p>
          For privacy questions, email support@vixalgotrading.com.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
